import { useMemo, useState } from "react";

import clsx from "clsx";
import { format, parse } from "date-fns";
import { sortBy } from "lodash";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { DataExportBuilderModal } from "@features/data-export/components/data-export-builder-modal";
import {
  type DataExportTarget,
  type SalesMetricsConfig,
  type SalesMetricsExportAggregation,
  type ScheduleConfig,
  type TimeFrame,
} from "@features/data-export/types";
import { FETCH_CUSTOM_GOALS } from "@gql/creative-analytics";
import { FETCH_SCHEDULED_EXPORT_DETAILS } from "@gql/data-export";
import {
  useFeatureFlags,
  useNorthbeamLazyQuery,
  useNorthbeamQuery,
} from "@hooks/http";
import {
  DataExportMethod,
  type FetchCustomGoals,
  type FetchScheduledExportDetails,
  type FetchScheduledExportDetailsVariables,
  type FetchScheduledExportDetails_me_scheduledExportDetails,
} from "@north-beam/nb-client-side";
import { getSalesPageMetricSpecs } from "@north-beam/nb-client-side/metrics";
import {
  AccountingModes,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { IconButton } from "@shared/buttons";

export const EditScheduledExportButtonWithModal = ({
  disabled = false,
  onClose,
  scheduledExportId,
}: {
  disabled?: boolean;
  onClose?(): void;
  scheduledExportId: string;
}) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    loadData,
    data: { data, loading },
  } = useNorthbeamLazyQuery<
    FetchScheduledExportDetails,
    FetchScheduledExportDetailsVariables
  >(FETCH_SCHEDULED_EXPORT_DETAILS, {
    onCompleted: () => {
      setTimeout(() => setIsModalOpen(true), 10);
    },
  });

  const scheduledExportDetails = data?.me?.scheduledExportDetails;

  return (
    <>
      <IconButton
        loading={loading}
        disabled={disabled}
        leftIcon={<i className="fa-regular fa-sharp fa-edit" />}
        type="button"
        className={clsx("btn-outline-primary w-[4rem]", {
          "hover:w-full transition-[width]": !disabled,
          "text-v2-disabled": disabled,
        })}
        size="sm"
        onClick={() => {
          if (data) {
            setIsModalOpen(true);
          } else {
            loadData({ variables: { id: scheduledExportId } });
          }
        }}
      >
        Edit
      </IconButton>
      {!!scheduledExportDetails && (
        <EditScheduledExportModal
          isOpen={isModalOpen}
          scheduledExportDetails={scheduledExportDetails}
          onClose={() => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onClose",
              additionalActionInfo: "Data Export Modal closed",
            });
            onClose?.();
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
};

const EditScheduledExportModal = ({
  scheduledExportDetails,
  isOpen,
  onClose,
}: {
  scheduledExportDetails: FetchScheduledExportDetails_me_scheduledExportDetails;
  isOpen: boolean;
  onClose?(): void;
}) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const { data: customGoalsData } =
    useNorthbeamQuery<FetchCustomGoals>(FETCH_CUSTOM_GOALS);

  const customGoals = useMemo(
    () => customGoalsData?.me.customGoals.goals ?? [],
    [customGoalsData?.me.customGoals],
  );
  const { customMetricData } = useCustomMetrics();

  const showV1Forecasts = useMemo(() => {
    return (
      scheduledExportDetails.timeGranularity.toLowerCase() !== "hourly" &&
      scheduledExportDetails?.defaultAttributionModels.includes(
        "northbeam_custom",
      ) &&
      scheduledExportDetails?.defaultAccountingModes.includes(
        AccountingModes.ACCRUAL,
      ) &&
      hasFeatureFlag("showV1Forecasts")
    );
  }, [
    scheduledExportDetails.timeGranularity,
    scheduledExportDetails?.defaultAttributionModels,
    scheduledExportDetails?.defaultAccountingModes,
    hasFeatureFlag,
  ]);

  const metricSpecs = useMemo(() => {
    return getSalesPageMetricSpecs(
      user,
      showV1Forecasts,
      customGoals,
      customMetricData,
    );
  }, [customMetricData, customGoals, showV1Forecasts, user]);

  const metricsSortedByPosition = sortBy(
    scheduledExportDetails.metrics,
    "position",
  );
  const metricsParsedAndSortedByPosition = metricsSortedByPosition.map(
    (metric) => ({
      id: metric.id,
      label: metric.label,
      attributionModels: new Set(metric.attributionModels),
      attributionWindows: new Set(
        metric.attributionWindows,
      ) as Set<IMCAttributionWindowDays>,
      accountingModes: new Set(
        metric.accountingModes,
      ) as Set<INBAccountingMode>,
    }),
  );

  const breakdownsSortedByPosition = sortBy(
    scheduledExportDetails.breakdowns,
    "position",
  );
  const timeFrameType = scheduledExportDetails.timeFrameType;

  const timeFrame =
    timeFrameType === "fixed"
      ? {
          type: "fixed",
          startDate: scheduledExportDetails.timeFrameStartDate,
          endDate: scheduledExportDetails.timeFrameEndDate,
        }
      : timeFrameType;

  const currentExportConfig: SalesMetricsConfig = {
    metrics: metricsParsedAndSortedByPosition,
    exportAggregation:
      scheduledExportDetails.exportAggregation as SalesMetricsExportAggregation,
    removeZeroSpend: scheduledExportDetails.removeZeroSpend,
    aggregateData: scheduledExportDetails.aggregateData,
    includeIds: scheduledExportDetails.includeIds,
    includeKindAndPlatform: scheduledExportDetails.includeKindAndPlatform,
    level: scheduledExportDetails.level as IGranularity,
    timeGranularity:
      scheduledExportDetails.timeGranularity as ITimeGranularityV2,
    breakdowns: breakdownsSortedByPosition,
    defaultAttributionModels: scheduledExportDetails.defaultAttributionModels,
    defaultAttributionWindows:
      scheduledExportDetails.defaultAttributionWindows as IMCAttributionWindowDays[],
    defaultAccountingModes:
      scheduledExportDetails.defaultAccountingModes as INBAccountingMode[],
    timeFrame: timeFrame as TimeFrame,
    fileName: scheduledExportDetails.exportFileName ?? "",
  };

  const currentScheduleConfig: ScheduleConfig = {
    name: scheduledExportDetails.scheduleName,
    frequency: scheduledExportDetails.frequency,
    timeFrames: scheduledExportDetails.schedules.map((schedule) => {
      return {
        day: schedule.localDay,
        time: format(
          parse(schedule.localTime, "HH:mm:ss", new Date()),
          "HH:mm",
        ),
      };
    }),
    recipients: scheduledExportDetails.recipients,
  };

  const isExternalGCSExport =
    scheduledExportDetails.exportMethod ===
    DataExportMethod.EXTERNAL_GCS_BUCKET;

  const initialStep = isExternalGCSExport
    ? "gcs-configurator"
    : "metrics-configurator";

  return (
    <DataExportBuilderModal
      isOpen={isOpen}
      initialStep={initialStep}
      existingScheduledExportId={scheduledExportDetails.id}
      initialExportMethod={scheduledExportDetails.exportMethod}
      initialExportMethodDetails={
        scheduledExportDetails.exportMethodDetails ?? undefined
      }
      initialExportConfig={currentExportConfig}
      initialScheduleConfig={currentScheduleConfig}
      target={scheduledExportDetails.target as DataExportTarget}
      availableMetricSpecs={metricSpecs}
      selectedMetricIds={metricsSortedByPosition.map((metric) => metric.id)}
      attributionModel={currentExportConfig.defaultAttributionModels[0]}
      attributionWindow={currentExportConfig.defaultAttributionWindows[0]}
      nbAccountingMode={currentExportConfig.defaultAccountingModes[0]}
      granularity={currentExportConfig.level}
      timeGranularity={currentExportConfig.timeGranularity}
      breakdowns={scheduledExportDetails?.breakdowns}
      dateRange={currentExportConfig.timeFrame}
      onClose={() => onClose?.()}
    />
  );
};
